import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { useLoadableState } from 'af-root/hooks/useLoadable';

import type { W_JobMaterial_SyncDiff_RM, W_JobMaterial_SyncDiff_VM } from 'ab-api/web/jobMaterial/syncDiff';
import type { W_Material_FindForJobImport_VM_Single } from 'ab-api/web/material/findForJobImport';

import { createExistsMap } from '@acceligentllc/shared/utils/array';

import { Button } from '@acceligentllc/storybook';

import { useSearchInputState } from 'af-components/Controls/SearchInput';
import CustomModal from 'af-components/CustomModal';

import * as JobMaterialActions from 'af-actions/jobMaterial';
import * as MaterialActions from 'af-actions/material';

import DiffCount from './DiffCount';
import GroupList from './GroupList';
import MaterialsList from './MaterialsList';
import styles from './styles.module.scss';
import SyncDiff from './SyncDiff';

interface OwnProps {
	jobId: number;
	showModal: boolean;
	title: string;
	close: () => void;
	onSubmit: () => void;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const MaterialsImportModal: React.FC<Props> = (props: Props) => {
	const {
		jobId,
		showModal,
		title,
		close,
		findMaterialGroups,
		findMaterialsForJobImport,
		syncJobMaterials,
		calculateJobMaterialsSyncDiff,
		onSubmit,
		findDefaultOnJobMaterials,
		findImportedIdsForJob,
	} = props;

	const [selectedGroup, setSelectedGroup] = React.useState<Nullable<string>>(null);
	const [importedMaterials, setImportedMaterials] = React.useState<Record<string, boolean>>({});
	const { value: groupSearch, onChange: setGroupSearch, reset: resetGroupSearch } = useSearchInputState();
	const { value: materialSearch, onChange: setMaterialSearch, reset: resetMaterialSearch } = useSearchInputState();
	const [syncDiff, setSyncDiff] = React.useState<Nullable<W_JobMaterial_SyncDiff_VM>>(null);
	const [currentlyImportedIds, setCurrentlyImportedIds] = React.useState<number[]>([]);

	const loadTotalJobMaterialCount = React.useCallback(async () => {
		const loaded = await findImportedIdsForJob(jobId);
		setCurrentlyImportedIds(loaded.ids);
	}, [findImportedIdsForJob, jobId]);

	React.useEffect(() => {
		if (showModal) {
			loadTotalJobMaterialCount();
		}
	}, [loadTotalJobMaterialCount, showModal]);

	const fetchMaterials = React.useCallback(async () => {
		if (!selectedGroup) {
			return null;
		}
		const data = await findMaterialsForJobImport({ jobId, group: selectedGroup });
		return data.materials;
	}, [findMaterialsForJobImport, jobId, selectedGroup]);

	const fetchGroups = React.useCallback(async () => {
		const data = await findMaterialGroups();
		return data.groups;
	}, [findMaterialGroups]);

	const { data: materials, isLoading: isLoadingMaterials, load: loadMaterials, reset: resetMaterials } = useLoadableState(fetchMaterials);
	const { data: groups, isLoading: isLoadingGroups, load: loadGroups, reset: resetGroups } = useLoadableState(fetchGroups);

	React.useEffect(() => {
		if (isLoadingGroups || !groups?.length) {
			return;
		}
		setSelectedGroup(groups[0].name);
	}, [groups, isLoadingGroups]);

	React.useEffect(() => {
		if (!showModal) {
			return;
		}
		loadMaterials();
	}, [loadMaterials, showModal]);

	React.useEffect(() => {
		if (!showModal) {
			return;
		}
		loadGroups();
	}, [loadGroups, showModal]);

	React.useEffect(() => {
		if (showModal) {
			return;
		}
		resetGroups();
		setSelectedGroup(null);
		resetMaterials();
		setImportedMaterials({});
		resetGroupSearch();
		resetMaterialSearch();
		setSyncDiff(null);
	}, [resetGroupSearch, resetGroups, resetMaterialSearch, resetMaterials, showModal]);

	const loadSyncDiff = React.useCallback(async () => {
		if (!Object.keys(importedMaterials).length) {
			return;
		}
		const rm = Object.keys(importedMaterials).reduce<W_JobMaterial_SyncDiff_RM>((_acc, _id) => {
			const isImported = importedMaterials[_id];
			if (isImported) {
				_acc.idsForImport.push(+_id);
			} else {
				_acc.idsForDelete.push(+_id);
			}
			return _acc;
		}, { idsForImport: [], idsForDelete: [] });
		const data = await calculateJobMaterialsSyncDiff(jobId, rm);
		setSyncDiff(data);
	}, [calculateJobMaterialsSyncDiff, importedMaterials, jobId]);

	const handleSelectJobDefaultMaterials = React.useCallback(async () => {
		const loaded = await findDefaultOnJobMaterials();
		setImportedMaterials((current) => {
			const jobDefaultMaterialMap = createExistsMap(loaded.materials, (_material) => _material.id);
			return {
				...current,
				...jobDefaultMaterialMap,
			};
		});
	}, [findDefaultOnJobMaterials]);

	const handleMaterialCheck = React.useCallback((material: W_Material_FindForJobImport_VM_Single) => {
		setImportedMaterials((current) => ({ ...current, [material.id]: (material.id in current) ? !current[material.id] : !material.imported }));
	}, []);

	const handleImportMultiple = React.useCallback((mats: W_Material_FindForJobImport_VM_Single[], value: boolean) => {
		setImportedMaterials((current) => mats.reduce<Record<string, boolean>>((_acc, _material) => {
			_acc[_material.id] = value;
			return _acc;
		}, { ...current }));
	}, []);

	const handleDiffDelete = React.useCallback((material: W_Material_FindForJobImport_VM_Single) => {
		setImportedMaterials((current) => {
			const next = { ...current };
			delete next[material.id];
			return next;
		});
	}, []);

	const materialsMergedWithImportData = React.useMemo(() => {
		if (!materials) {
			return null;
		}
		return materials.map((_material) => ({
			..._material,
			imported: (_material.id in importedMaterials) ? importedMaterials[_material.id] : _material.imported,
		}));
	}, [importedMaterials, materials]);

	const importCount = React.useMemo(() => {
		const count = currentlyImportedIds.reduce((_count, _id) => {
			if (importedMaterials[_id] === false) {
				return _count - 1;
			}
			return _count;
		}, currentlyImportedIds.length);

		const currentlyImportedMap = createExistsMap(currentlyImportedIds, (id) => id);
		return Object.keys(importedMaterials).reduce<number>((_count, _id) => {
			if (!currentlyImportedMap[_id] && importedMaterials[_id]) {
				return _count + 1;
			}
			return _count;
		}, count);
	}, [currentlyImportedIds, importedMaterials]);

	const { added: addedCount, removed: removedCount } = React.useMemo(() => {
		const currentlyImportedMap = createExistsMap(currentlyImportedIds, (id) => id);
		return Object.keys(importedMaterials).reduce<{ added: number; removed: number; }>((_acc, _id) => {
			if (importedMaterials[_id] === true && !currentlyImportedMap[_id]) {
				_acc.added += 1;
			} else if (importedMaterials[_id] === false && currentlyImportedMap[_id]) {
				_acc.removed += 1;
			}
			return _acc;
		}, { added: 0, removed: 0 });
	}, [currentlyImportedIds, importedMaterials]);

	const handleSyncJobMaterials = React.useCallback(async () => {
		close();
		await syncJobMaterials(jobId, { materials: importedMaterials });
		onSubmit();
	}, [close, importedMaterials, jobId, onSubmit, syncJobMaterials]);

	const handleSyncPreview = React.useCallback(async () => {
		loadSyncDiff();
	}, [loadSyncDiff]);

	const handleSyncPreviewBack = React.useCallback(() => {
		setSyncDiff(null);
	}, []);

	const handleClearTotalSelected = React.useCallback(() => {
		setImportedMaterials((_current) => {
			const next = Object.keys(_current).reduce((_acc, _id) => {
				_acc[_id] = false;
				return _acc;
			}, { ..._current });

			return currentlyImportedIds.reduce<Record<string, boolean>>((_acc, _id) => {
				_acc[_id] = false;
				return _acc;
			}, next);
		});
	}, [currentlyImportedIds]);

	const previewChangesDisabled = React.useMemo(() => !addedCount && !removedCount, [addedCount, removedCount]);

	React.useEffect(() => {
		if (previewChangesDisabled) {
			setSyncDiff(null);
		}
	}, [previewChangesDisabled]);

	const { materialsForImportDiff, materialsForDeleteDiff } = React.useMemo(() => {
		return {
			materialsForImportDiff: syncDiff?.materialsForImport?.filter((_mat) => _mat.id in importedMaterials) ?? [],
			materialsForDeleteDiff: syncDiff?.materialsForDelete?.filter((_mat) => _mat.id in importedMaterials) ?? [],
		};
	}, [importedMaterials, syncDiff?.materialsForDelete, syncDiff?.materialsForImport]);

	return (
		<CustomModal contentClassName={styles.modal} dialogClassName={styles.dialog} modalStyle="warning" showModal={showModal} size="xl">
			<CustomModal.Header
				closeModal={close}
				title={title}
			/>
			{!syncDiff
				? (
					<>
						<CustomModal.Body className={styles.modalBody}>
							<div className={styles.instructions}>Please select which materials you would like to add to this Job.</div>
							<div className={styles.totalSelectedContainer}>
								<div className={styles.counters}>
									<div className={styles.totalSelectedCounter}>Total materials selected across all groups: {importCount}</div>
									<DiffCount count={addedCount} type="added" />
									<DiffCount count={removedCount} type="removed" />
								</div>
								<Button
									disabled={importCount === 0}
									icon="Clear"
									label="Clear All Selected"
									onClick={handleClearTotalSelected}
									style="link"
									type="button"
								/>
							</div>
							<div className={styles.content}>
								<GroupList
									groups={groups}
									isLoading={isLoadingGroups}
									onSearchChange={setGroupSearch}
									onSelect={setSelectedGroup}
									search={groupSearch}
									selectedGroup={selectedGroup}
								/>
								<MaterialsList
									className={styles.materialsList}
									isLoading={isLoadingMaterials}
									materials={materialsMergedWithImportData}
									materialSearch={materialSearch}
									setImportForMultiple={handleImportMultiple}
									setMaterialSearch={setMaterialSearch}
									toggleImport={handleMaterialCheck}
								/>
							</div>
						</CustomModal.Body>
						<CustomModal.Footer className={styles.footer}>
							<div className={styles.actionGroup}>
								<Button
									label="Select all Job Default Materials"
									onClick={handleSelectJobDefaultMaterials}
									style="primary"
								/>
							</div>
							<div className={styles.actionGroup}>
								<Button
									label="Close"
									onClick={close}
									style="secondary"
								/>
								<Button
									disabled={previewChangesDisabled}
									label="Preview Changes"
									onClick={handleSyncPreview}
									style="primary"
								/>
							</div>
						</CustomModal.Footer>
					</>
				) : (
					<>
						<CustomModal.Body className={styles.modalBody}>
							<div className={styles.content}>
								<SyncDiff
									materialsForImport={materialsForImportDiff}
									materialsForRemove={materialsForDeleteDiff}
									onDelete={handleDiffDelete}
								/>
							</div>
						</CustomModal.Body>
						<CustomModal.Footer className={styles.footer}>
							<div className={styles.actionGroup} />
							<div className={styles.actionGroup}>
								<Button
									label="Back"
									onClick={handleSyncPreviewBack}
									style="secondary"
								/>
								<Button
									label="Save Changes"
									onClick={handleSyncJobMaterials}
									style="primary"
								/>
							</div>
						</CustomModal.Footer>
					</>
				)}
		</CustomModal>
	);
};

const mapDispatchToProps = () => {
	return {
		findMaterialGroups: MaterialActions.findMaterialGroups,
		findMaterialsForJobImport: MaterialActions.findMaterialsForJobImport,
		syncJobMaterials: JobMaterialActions.syncJobMaterials,
		calculateJobMaterialsSyncDiff: JobMaterialActions.calculateJobMaterialsSyncDiff,
		findDefaultOnJobMaterials: MaterialActions.findDefaultOnJobMaterials,
		findImportedIdsForJob: JobMaterialActions.findImportedIdsForJob,
	};
};

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	connector,
	React.memo
);

export default enhance(MaterialsImportModal);
